import { useState, useEffect } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Calendar, Briefcase, GraduationCap, 
  Target, Code, Building, Award, Camera, Edit2, Save, X, Eye, Plus, Trash2,
  MapPin, Lightbulb, Star, ChevronRight, Info, FileText, Upload, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import toast from 'react-hot-toast';
import apiService from '@services/api/api.service';
import profileService from '@services/profile.service';
import profileExtendedService from '@services/profileExtended.service';
import mentorExpertiseService from '@services/mentorExpertise.service';
import { getImageUrl } from '@utils/imageUtils';
import CareerFocusSection from '@components/profile/CareerFocusSection';
import SkillsSection from '@components/profile/SkillsSection';
import PersonalityTraitsSection from '@components/profile/PersonalityTraitsSection';
import ExperienceSection from '@components/profile/ExperienceSection';
import BankDetailsSection from '@components/profile/BankDetailsSection';
import { useDocument } from '@hooks/useDocument';
import { DOCUMENT_TYPES, DOCUMENT_STATUS } from '@constants';

const studentSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().optional(),
  careerGoal: z.string().max(300).optional(),
  currentDesignation: z.string().max(100).optional(),
  interestedIn: z.array(z.string()).optional(),
});

const mentorSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().optional(),
  careerGoal: z.string().max(300).optional(),
  currentCompany: z.string().optional(),
  currentPosition: z.string().optional(),
  yearsOfExperience: z.number().min(0).max(50).optional().or(z.string().optional()),
});

const Profile = () => {
  const { user: authUser, refreshUser } = useAuth();
  const { uploadDocument, getMyDocuments } = useDocument();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [expertiseAreas, setExpertiseAreas] = useState([]);
  const [newExpertise, setNewExpertise] = useState('');
  const [interestedIn, setInterestedIn] = useState([]);
  const [newInterest, setNewInterest] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [awards, setAwards] = useState([]);
  const [uploadingAward, setUploadingAward] = useState(false);
  const [acquiredDegrees, setAcquiredDegrees] = useState([]);
  const [customDegrees, setCustomDegrees] = useState([]);
  const [newCustomDegree, setNewCustomDegree] = useState('');
  const [mentorExpertiseList, setMentorExpertiseList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [showExpertiseForm, setShowExpertiseForm] = useState(false);
  const [editingExpertiseId, setEditingExpertiseId] = useState(null);
  const [expertiseFormData, setExpertiseFormData] = useState({
    industryName: '',
    industryId: null,
    jobTitle: '',
    isCustomJob: false,
    expertiseDescription: ''
  });
  const [jobProfiles, setJobProfiles] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [address, setAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    street: '',
    city: '',
    district: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [viewingExpertise, setViewingExpertise] = useState(null);
  
  // Document management states
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});

  const degreeOptions = [
    { id: 'diploma', name: 'Diploma' },
    { id: 'graduation', name: 'Graduation' },
    { id: 'masters', name: 'Masters' },
    { id: 'phd', name: 'PHD' },
    { id: 'post_doc', name: 'Post Doc' },
    { id: 'pgdm', name: 'PGDM' },
    { id: 'executive', name: 'Executive' }
  ];

  const isStudent = authUser?.role === 'student';
  const isMentor = authUser?.role === 'mentor';

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    resolver: zodResolver(isStudent ? studentSchema : mentorSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      careerGoal: '',
      currentDesignation: '',
      currentCompany: '',
      currentPosition: '',
      yearsOfExperience: '',
    },
  });

  useEffect(() => {
    fetchProfile();
    fetchAddresses();
    if (isMentor) {
      fetchMentorExpertise();
      fetchIndustries();
      fetchDocuments();
    }
  }, []);

  useEffect(() => {
    if (profile) {
      Object.keys(profile).forEach(key => {
        if (profile[key] !== undefined && profile[key] !== null) {
          setValue(key, profile[key]);
        }
      });
    }
  }, [profile, setValue]);

  const fetchProfile = async () => {
    try {
      const response = await apiService.get('/profile/me');
      if (response.success) {
        setProfile(response?.data?.data);
        setExpertiseAreas(response?.data?.data?.expertiseAreas || []);
        setAwards(response?.data?.data?.awards || []);
        setAcquiredDegrees(response?.data?.data?.acquiredDegrees || []);
        setCustomDegrees(response?.data?.data?.customDegrees || []);
        setInterestedIn(response?.data?.data?.interestedIn || []);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await profileExtendedService.getAddresses();
      const addressData = response.data?.data || response.data || [];
      const addressArray = Array.isArray(addressData) ? addressData : [];
      setAddress(addressArray.length > 0 ? addressArray[0] : null);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
      setAddress(null);
    }
  };

  const handleAddressSubmit = async () => {
    if (!addressFormData.city) {
      toast.error('City is required');
      return;
    }
    
    setSavingAddress(true);
    try {
      if (address?.id) {
        await profileExtendedService.updateAddress(address.id, { ...addressFormData, isPrimary: true });
        toast.success('Address updated successfully');
      } else {
        await profileExtendedService.addAddress({ ...addressFormData, isPrimary: true });
        toast.success('Address added successfully');
      }
      
      await fetchAddresses();
      setShowAddressForm(false);
      setAddressFormData({ street: '', city: '', district: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleEditAddress = () => {
    if (address) {
      setAddressFormData({
        street: address.street || '',
        city: address.city || '',
        district: address.district || ''
      });
      setShowAddressForm(true);
    }
  };

  const handleDeleteAddress = async () => {
    if (!address?.id) return;
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      await profileExtendedService.deleteAddress(address.id);
      toast.success('Address deleted successfully');
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const fetchMentorExpertise = async () => {
    try {
      const response = await mentorExpertiseService.getMyExpertise();
      if (response.success) {
        const expertiseData = response.data?.data || response.data || [];
        setMentorExpertiseList(Array.isArray(expertiseData) ? expertiseData : []);
      }
    } catch (error) {
      console.error('Failed to load mentor expertise:', error);
      setMentorExpertiseList([]);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await mentorExpertiseService.getIndustries();
      if (response.success) {
        const industriesData = response.data?.data || response.data || [];
        setIndustries(Array.isArray(industriesData) ? industriesData : []);
      }
    } catch (error) {
      console.error('Failed to load industries:', error);
      setIndustries([]);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await getMyDocuments();
      if (response.success) {
        const docs = response.data?.data || response.data || [];
        setDocuments(docs);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const handleFileSelect = (docType, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, and PDF files are allowed');
      return;
    }
    setSelectedFiles(prev => ({ ...prev, [docType]: file }));
  };

  const handleDocumentUpload = async (docType) => {
    const file = selectedFiles[docType];
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    setUploadingDoc(docType);
    try {
      const response = await uploadDocument(docType, file);
      if (response.success) {
        toast.success('Document uploaded successfully');
        setSelectedFiles(prev => ({ ...prev, [docType]: null }));
        fetchDocuments();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const getDocumentLabel = (type) => {
    const labels = {
      [DOCUMENT_TYPES.AADHAR]: 'Aadhar Card',
      [DOCUMENT_TYPES.DEGREE_CERTIFICATE]: 'Degree Certificate',
      [DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE]: 'Experience Certificate',
    };
    return labels[type] || type;
  };

  const getUploadedDoc = (type) => {
    return documents.find(doc => doc.documentType === type);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case DOCUMENT_STATUS.APPROVED:
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case DOCUMENT_STATUS.REJECTED:
        return <XCircle className="w-4 h-4 text-red-600" />;
      case DOCUMENT_STATUS.PENDING:
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case DOCUMENT_STATUS.APPROVED:
        return 'Approved';
      case DOCUMENT_STATUS.REJECTED:
        return 'Rejected';
      case DOCUMENT_STATUS.PENDING:
        return 'Pending Review';
      default:
        return 'Not Uploaded';
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const payload = {
        ...data,
        expertiseAreas: isMentor ? expertiseAreas : undefined,
        awards: awards,
        acquiredDegrees: isStudent ? acquiredDegrees : undefined,
        customDegrees: isStudent ? customDegrees : undefined,
        interestedIn: isStudent ? interestedIn : undefined,
      };
      const response = await apiService.put('/profile', payload);
      if (response.success) {
        await fetchProfile();
        setIsEditing(false);
        toast.success('Profile updated successfully!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpertise = () => {
    if (newExpertise.trim() && !expertiseAreas.includes(newExpertise.trim())) {
      setExpertiseAreas([...expertiseAreas, newExpertise.trim()]);
      setNewExpertise('');
    }
  };

  const handleRemoveExpertise = (expertise) => {
    setExpertiseAreas(expertiseAreas.filter(e => e !== expertise));
  };

  const handleAddInterest = () => {
    if (newInterest.trim() && !interestedIn.includes(newInterest.trim())) {
      setInterestedIn([...interestedIn, newInterest.trim()]);
      setNewInterest('');
    }
  };

  const handleRemoveInterest = (interest) => {
    setInterestedIn(interestedIn.filter(i => i !== interest));
  };

  const handleIndustryChange = async (e) => {
    const selectedIndustry = e.target.value;
    setExpertiseFormData({ ...expertiseFormData, industryName: selectedIndustry, jobTitle: '', isCustomJob: false });
    if (selectedIndustry && selectedIndustry !== 'custom') {
      setLoadingJobs(true);
      try {
        const response = await mentorExpertiseService.getJobProfilesByIndustry(selectedIndustry);
        if (response.success) {
          const jobProfilesData = response.data?.data || response.data || [];
          setJobProfiles(Array.isArray(jobProfilesData) ? jobProfilesData : []);
        }
      } catch (error) {
        console.error('Failed to load job profiles:', error);
        setJobProfiles([]);
      } finally {
        setLoadingJobs(false);
      }
    } else {
      setJobProfiles([]);
    }
  };

  const handleJobChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setExpertiseFormData({ ...expertiseFormData, jobTitle: '', isCustomJob: true, industryId: null });
    } else {
      const selectedJob = jobProfiles.find(job => job.id === value);
      setExpertiseFormData({ 
        ...expertiseFormData, 
        jobTitle: selectedJob?.jobProfile || '', 
        isCustomJob: false,
        industryId: selectedJob?.id || null
      });
    }
  };

  const handleExpertiseSubmit = async () => {
    if (!expertiseFormData.industryName || !expertiseFormData.jobTitle || !expertiseFormData.expertiseDescription) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      if (editingExpertiseId) {
        const response = await mentorExpertiseService.updateExpertise(editingExpertiseId, expertiseFormData);
        if (response.success || response.data?.success) {
          toast.success('Expertise updated successfully');
          await fetchMentorExpertise();
          resetExpertiseForm();
        } else {
          toast.error(response.data?.message || 'Failed to update expertise');
        }
      } else {
        const response = await mentorExpertiseService.createExpertise(expertiseFormData);
        if (response.success || response.data?.success) {
          toast.success('Expertise added successfully');
          await fetchMentorExpertise();
          resetExpertiseForm();
        } else {
          toast.error(response.data?.message || 'Failed to add expertise');
        }
      }
    } catch (error) {
      console.error('Error saving expertise:', error);
      toast.error(error.message || 'Failed to save expertise');
    }
  };

  const handleEditExpertise = (expertise) => {
    setExpertiseFormData({
      industryName: expertise.industryName,
      industryId: expertise.industryId,
      jobTitle: expertise.jobTitle,
      isCustomJob: expertise.isCustomJob,
      expertiseDescription: expertise.expertiseDescription
    });
    setEditingExpertiseId(expertise.id);
    setShowExpertiseForm(true);
    if (expertise.industryName && expertise.industryName !== 'custom') {
      mentorExpertiseService.getJobProfilesByIndustry(expertise.industryName)
        .then(response => {
          if (response.success) {
            const jobProfilesData = response.data?.data || response.data || [];
            setJobProfiles(Array.isArray(jobProfilesData) ? jobProfilesData : []);
          }
        });
    }
  };

  const handleDeleteExpertise = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expertise?')) return;
    try {
      const response = await mentorExpertiseService.deleteExpertise(id);
      if (response.success) {
        toast.success('Expertise deleted successfully');
        fetchMentorExpertise();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete expertise');
    }
  };

  const resetExpertiseForm = () => {
    setExpertiseFormData({
      industryName: '',
      industryId: null,
      jobTitle: '',
      isCustomJob: false,
      expertiseDescription: ''
    });
    setJobProfiles([]);
    setShowExpertiseForm(false);
    setEditingExpertiseId(null);
  };

  const handleDegreeToggle = (degreeId) => {
    if (acquiredDegrees.includes(degreeId)) {
      setAcquiredDegrees(acquiredDegrees.filter(id => id !== degreeId));
    } else {
      setAcquiredDegrees([...acquiredDegrees, degreeId]);
    }
  };

  const handleAddCustomDegree = () => {
    if (newCustomDegree.trim() && !customDegrees.includes(newCustomDegree.trim())) {
      setCustomDegrees([...customDegrees, newCustomDegree.trim()]);
      setNewCustomDegree('');
    }
  };

  const handleRemoveCustomDegree = (degree) => {
    setCustomDegrees(customDegrees.filter(d => d !== degree));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }
    try {
      setUploadingImage(true);
      const response = await profileService.uploadProfilePicture(file);
      if (response.success) {
        setProfile({ ...profile, profilePicture: response?.data?.data?.profilePicture });
        await refreshUser();
        toast.success('Profile picture updated successfully!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddAward = () => {
    setAwards([...awards, { type: 'accolades', image: null, note: '' }]);
  };

  const handleRemoveAward = (index) => {
    setAwards(awards.filter((_, i) => i !== index));
  };

  const handleAwardTypeChange = (index, type) => {
    const updatedAwards = [...awards];
    updatedAwards[index].type = type;
    setAwards(updatedAwards);
  };

  const handleAwardNoteChange = (index, note) => {
    const updatedAwards = [...awards];
    updatedAwards[index].note = note;
    setAwards(updatedAwards);
  };

  const handleAwardImageUpload = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }
    try {
      setUploadingAward(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiService.post('/upload/award', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.success) {
        const updatedAwards = [...awards];
        updatedAwards[index].image = response?.data?.data?.url;
        setAwards(updatedAwards);
        toast.success('Award image uploaded successfully!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploadingAward(false);
    }
  };

  const getInitials = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName[0]}${profile.lastName[0]}`.toUpperCase();
    }
    return authUser?.email?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <div className="w-full">
      {/* Compact Profile Header */}
      <Card className="mb-4 p-4">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          {/* Avatar */}
          <div className="flex-shrink-0 self-center sm:self-start">
            <div className="relative">
                {profile?.profilePicture ? (
                  <>
                    <img
                      src={getImageUrl(profile?.profilePicture)}
                      alt="Profile"
                      className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div
                      className="w-20 h-20 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-lg items-center justify-center border-2 border-gray-200"
                      style={{ display: 'none' }}
                    >
                      <span className="text-2xl font-bold text-white">{getInitials()}</span>
                    </div>
                  </>
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-lg flex items-center justify-center border-2 border-gray-200">
                      <span className="text-2xl font-bold text-white">{getInitials()}</span>
                  </div>
                )}
              <label
                htmlFor="profile-picture-upload"
                className="absolute -bottom-1 -right-1 w-7 h-7 bg-secondary-500 hover:bg-secondary-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors cursor-pointer"
              >
                {uploadingImage ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-3.5 h-3.5" />
                )}
                <input
                  id="profile-picture-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImage}
                />
              </label>
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1 min-w-0 w-full">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-primary-800 break-words">
                  {profile?.firstName && profile?.lastName
                    ? `${profile.firstName} ${profile.lastName}`
                    : authUser?.email}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-xs text-primary-600 capitalize font-medium">{authUser?.role}</span>
                  <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                  <div className="inline-flex items-center gap-1.5 text-xs text-green-700">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    Active
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex-shrink-0 w-full sm:w-auto">
                {!isEditing ? (
                  <div className="flex flex-wrap gap-2">
                    {(authUser?.id || profile?.id) && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 sm:flex-none justify-center"
                        onClick={() => {
                          const path = authUser?.role === 'mentor' 
                            ? `/mentor-profile/${authUser?.id || profile?.id}` 
                            : `/portfolio/${authUser?.id || profile?.id}`;
                          navigate(path, { state: { from: 'profile' } });
                        }}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        <span className="hidden xs:inline sm:inline">
                          {authUser?.role === 'mentor' ? 'View Public Profile' : 'View Public Portfolio'}
                        </span>
                        <span className="inline xs:hidden sm:hidden">View Public</span>
                      </Button>
                    )}
                    <Button onClick={() => setIsEditing(true)} variant="primary" size="sm" className="flex-1 sm:flex-none justify-center">
                      <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                      Edit
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => setIsEditing(false)} variant="outline" size="sm" className="flex-1 sm:flex-none justify-center">
                      <X className="w-3.5 h-3.5 mr-1.5" />
                      Cancel
                    </Button>
                    <Button onClick={handleSubmit(onSubmit)} variant="primary" loading={loading} size="sm" className="flex-1 sm:flex-none justify-center">
                      <Save className="w-3.5 h-3.5 mr-1.5" />
                      Save
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Info - Compact Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 mt-3 text-xs">
              <div className="flex items-center gap-1.5 text-gray-600 min-w-0">
                <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate">{authUser?.email}</span>
              </div>
              {authUser?.phone && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>{authUser.phone}</span>
                </div>
              )}
              {profile?.dateOfBirth && (
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>{profile.dateOfBirth}</span>
                </div>
              )}
            </div>

            {/* Education/Role Tags - Inline */}
            {(isStudent && (acquiredDegrees.length > 0 || customDegrees.length > 0)) && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <div className="flex flex-wrap gap-1">
                  {acquiredDegrees.map((d) => {
                    const deg = degreeOptions.find(opt => opt.id === d);
                    return deg ? (
                      <span key={d} className="px-2 py-0.5 bg-primary-50 text-primary-700 border border-primary-200 rounded text-xs">
                        {deg.name}
                      </span>
                    ) : null;
                  })}
                  {customDegrees.map((d, i) => (
                    <span key={i} className="px-2 py-0.5 bg-secondary-50 text-secondary-700 border border-secondary-200 rounded text-xs">
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isMentor && profile?.currentCompany && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                  {profile.currentPosition && (
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                      <span className="font-medium">{profile.currentPosition}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Building className="w-3.5 h-3.5 text-gray-400" />
                    <span>{profile.currentCompany}</span>
                  </div>
                  {profile.yearsOfExperience && (
                    <div className="flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5 text-gray-400" />
                      <span>{profile.yearsOfExperience}y exp</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Form Sections */}
      <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-3">

              {/* Basic Information */}
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-primary-800 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-primary-600" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Input
                    label="First Name"
                    placeholder="Enter first name"
                    disabled={!isEditing}
                    error={errors.firstName?.message}
                    {...register('firstName')}
                  />
                  <Input
                    label="Last Name"
                    placeholder="Enter last name"
                    disabled={!isEditing}
                    error={errors.lastName?.message}
                    {...register('lastName')}
                  />
                  <Input
                    label="Date of Birth"
                    type="date"
                    disabled={!isEditing}
                    error={errors.dateOfBirth?.message}
                    {...register('dateOfBirth')}
                  />
                </div>
                
                {isStudent && (
                  <>
                    <div className="mt-3">
                      <Input
                        label="Current Designation"
                        placeholder="e.g., Software Engineer, Student, Lawyer, etc."
                        disabled={!isEditing}
                        error={errors.currentDesignation?.message}
                        {...register('currentDesignation')}
                      />
                    </div>

                    {/* Interested In Section */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Interested In
                      </label>
                      {interestedIn.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {interestedIn.map((interest, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-md text-sm"
                            >
                              {interest}
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveInterest(interest)}
                                  className="hover:text-purple-900"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                      {isEditing && (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={newInterest}
                            onChange={(e) => setNewInterest(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddInterest())}
                            placeholder="e.g., AI/ML, Product, Startups, FinTech"
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <Button
                            type="button"
                            onClick={handleAddInterest}
                            variant="outline"
                            size="sm"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Location Section */}
                <div className="mt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-gray-500" />
                      Location
                    </h4>
                    {isEditing && !showAddressForm && !address && (
                      <Button type="button" onClick={() => setShowAddressForm(true)} variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Address
                      </Button>
                    )}
                  </div>

                  {showAddressForm && isEditing && (
                    <div className="mb-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-xs font-medium text-gray-700">
                          {address ? 'Edit Address' : 'Add Address'}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddressForm(false);
                            setAddressFormData({ street: '', city: '', district: '' });
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Street
                          </label>
                          <input
                            type="text"
                            value={addressFormData.street}
                            onChange={(e) => setAddressFormData({ ...addressFormData, street: e.target.value })}
                            className="w-full px-2.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                            placeholder="Street address"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={addressFormData.city}
                            onChange={(e) => setAddressFormData({ ...addressFormData, city: e.target.value })}
                            className="w-full px-2.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                            placeholder="City"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            District
                          </label>
                          <input
                            type="text"
                            value={addressFormData.district}
                            onChange={(e) => setAddressFormData({ ...addressFormData, district: e.target.value })}
                            className="w-full px-2.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                            placeholder="District"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-2">
                        <Button
                          type="button"
                          onClick={handleAddressSubmit}
                          variant="primary"
                          size="sm"
                          loading={savingAddress}
                        >
                          {address ? 'Update' : 'Add'}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            setShowAddressForm(false);
                            setAddressFormData({ street: '', city: '', district: '' });
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {!address && !showAddressForm ? (
                    <p className="text-xs text-gray-400 py-1">
                      No address added yet.{isEditing && ' Click "Add Address" to get started.'}
                    </p>
                  ) : address && !showAddressForm ? (
                    <div className="p-2 border border-gray-200 rounded-md bg-gray-50">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 break-words">
                            {address.street && `${address.street}, `}
                            {address.city}
                            {address.district && `, ${address.district}`}
                          </p>
                        </div>
                        {isEditing && (
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={handleEditAddress}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded transition"
                              title="Edit"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteAddress}
                              className="p-1 text-red-500 hover:bg-red-50 rounded transition"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Education Section - Only for Students */}
                {isStudent && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                      <GraduationCap className="w-3.5 h-3.5 text-gray-500" />
                      Education
                    </h4>
                    {/* Acquired Degrees */}
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1.5">Select degrees acquired</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                        {degreeOptions.map((degree) => (
                          <label
                            key={degree.id}
                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md border transition-all cursor-pointer text-xs ${
                              !isEditing ? 'opacity-60 cursor-not-allowed' : 'hover:border-secondary-400'
                            } ${
                              acquiredDegrees.includes(degree.id)
                                ? 'border-secondary-500 bg-secondary-50 text-secondary-700 font-medium'
                                : 'border-gray-200 bg-white text-gray-600'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={acquiredDegrees.includes(degree.id)}
                              onChange={() => handleDegreeToggle(degree.id)}
                              disabled={!isEditing}
                              className="w-3 h-3 text-secondary-500 rounded flex-shrink-0"
                            />
                            {degree.name}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Custom Degrees */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">Add custom degrees</p>
                      {customDegrees.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {customDegrees.map((degree, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary-100 border border-secondary-200 text-secondary-700 rounded text-xs"
                            >
                              {degree}
                              {isEditing && (
                                <button type="button" onClick={() => handleRemoveCustomDegree(degree)} className="hover:text-secondary-900">
                                  <X className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                      {isEditing && (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={newCustomDegree}
                            onChange={(e) => setNewCustomDegree(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomDegree())}
                            placeholder="Type degree name"
                            className="flex-1 px-2.5 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                          />
                          <Button type="button" onClick={handleAddCustomDegree} variant="outline" size="sm">
                            Add
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Bio Section */}
                {true && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                      <Target className="w-3.5 h-3.5 text-gray-500" />
                      Bio
                    </h4>
                    {isEditing ? (
                      <div>
                        <textarea
                          placeholder="Hey, I'm a software engineer..."
                          disabled={!isEditing}
                          maxLength={300}
                          rows={2}
                          className="w-full px-2.5 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900 resize-none"
                          {...register('careerGoal')}
                        />
                        {errors.careerGoal?.message && (
                          <p className="mt-1 text-xs text-red-500">{errors.careerGoal.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">Max 300 characters</p>
                      </div>
                    ) : (
                      <div className="p-2 border border-gray-200 rounded-md bg-gray-50">
                        {profile?.careerGoal ? (
                          <p className="text-sm text-gray-800 leading-relaxed break-words">{profile.careerGoal}</p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No bio added yet</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Awards & Honour Section */}
                <div className="mt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <Award className="w-3.5 h-3.5 text-gray-500" />
                      Awards & Honour
                    </h4>
                    {isEditing && (
                      <Button type="button" onClick={handleAddAward} variant="outline" size="sm">
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add
                      </Button>
                    )}
                  </div>

                  {awards.length === 0 ? (
                    <p className="text-xs text-gray-400 py-2">
                      No awards added yet.{isEditing && ' Click "Add" to get started.'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {awards.map((award, index) => (
                        <div key={index} className="p-2.5 border border-gray-200 rounded-md bg-gray-50">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                              <select
                                disabled={!isEditing}
                                value={award.type}
                                onChange={(e) => handleAwardTypeChange(index, e.target.value)}
                                className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:bg-gray-100 disabled:cursor-not-allowed border-gray-300 bg-white text-gray-900"
                              >
                                <option value="accolades">Accolades</option>
                                <option value="achievements">Achievements</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Image</label>
                              <div className="flex flex-wrap items-center gap-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  disabled={!isEditing}
                                  onChange={(e) => handleAwardImageUpload(index, e)}
                                  className="max-w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50"
                                />
                                {award.image && (
                                  <img src={getImageUrl(award.image)} alt="Award" className="w-8 h-8 object-cover rounded border border-gray-200 flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Note</label>
                            <textarea
                              rows={2}
                              disabled={!isEditing}
                              value={award.note}
                              onChange={(e) => handleAwardNoteChange(index, e.target.value)}
                              placeholder="Add a note..."
                              className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:bg-gray-100 disabled:cursor-not-allowed border-gray-300 bg-white text-gray-900 resize-none"
                            />
                          </div>
                          {isEditing && (
                            <div className="mt-2 flex justify-end">
                              <Button type="button" onClick={() => handleRemoveAward(index)} variant="danger" size="sm">
                                <Trash2 className="w-3 h-3 mr-1" />
                                Remove
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>




              {/* Professional Experience & Areas of Expertise - Mentor */}
              {isMentor && (
                <Card className="p-4">
                  <h3 className="text-sm font-semibold text-primary-800 mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-primary-600" />
                    Professional Experience
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                    <Input
                      label="Current Company"
                      placeholder="Your current organization"
                      disabled={!isEditing}
                      error={errors.currentCompany?.message}
                      {...register('currentCompany')}
                    />
                    <Input
                      label="Current Position"
                      placeholder="Your current role"
                      disabled={!isEditing}
                      error={errors.currentPosition?.message}
                      {...register('currentPosition')}
                    />
                    <Input
                      label="Years of Experience"
                      type="text"
                      placeholder="Total years"
                      disabled={!isEditing}
                      error={errors.yearsOfExperience?.message}
                      {...register('yearsOfExperience')}
                    />
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <h3 className="text-sm font-semibold text-primary-800 flex items-center gap-2">
                        <Award className="w-4 h-4 text-primary-600" />
                        Areas of Expertise
                      </h3>
                      {!showExpertiseForm && (
                        <Button type="button" onClick={() => setShowExpertiseForm(true)} variant="outline" size="sm">
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Add
                        </Button>
                      )}
                    </div>

                    {showExpertiseForm && (
                      <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Industry <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={expertiseFormData.industryName}
                              onChange={handleIndustryChange}
                              className="w-full px-2.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                            >
                              <option value="">Select Industry</option>
                              {Array.isArray(industries) && industries.map((industry, idx) => (
                                <option key={idx} value={industry}>{industry}</option>
                              ))}
                              <option value="custom">Custom Industry</option>
                            </select>
                          </div>

                          {expertiseFormData.industryName === 'custom' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Custom Industry <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={expertiseFormData.industryName === 'custom' ? '' : expertiseFormData.industryName}
                                onChange={(e) => setExpertiseFormData({ ...expertiseFormData, industryName: e.target.value })}
                                className="w-full px-2.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                                placeholder="Industry name"
                              />
                            </div>
                          )}

                          {expertiseFormData.industryName && expertiseFormData.industryName !== 'custom' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Job Profile <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={expertiseFormData.isCustomJob ? 'custom' : expertiseFormData.industryId || ''}
                                onChange={handleJobChange}
                                className="w-full px-2.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                                disabled={loadingJobs}
                              >
                                <option value="">Select Job Profile</option>
                                {Array.isArray(jobProfiles) && jobProfiles.map((job) => (
                                  <option key={job.id} value={job.id}>{job.jobProfile}</option>
                                ))}
                                <option value="custom">Custom Job Title</option>
                              </select>
                            </div>
                          )}

                          {(expertiseFormData.isCustomJob || expertiseFormData.industryName === 'custom') && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Job Title <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={expertiseFormData.jobTitle}
                                onChange={(e) => setExpertiseFormData({ ...expertiseFormData, jobTitle: e.target.value })}
                                className="w-full px-2.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900"
                                placeholder="Job title"
                              />
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                            <label className="block text-xs font-medium text-gray-700">
                              Expertise Description <span className="text-red-500">*</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => setShowTemplateModal(true)}
                              className="flex items-center gap-1 px-1.5 py-0.5 text-xs text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors self-start"
                              title="View template"
                            >
                              <Info className="w-3.5 h-3.5" />
                              Template
                            </button>
                          </div>
                          <textarea
                            value={expertiseFormData.expertiseDescription}
                            onChange={(e) => setExpertiseFormData({ ...expertiseFormData, expertiseDescription: e.target.value })}
                            rows={4}
                            className="w-full px-2.5 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 border-gray-300 bg-white text-gray-900 resize-none"
                            placeholder="Describe your expertise..."
                          />
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button type="button" onClick={handleExpertiseSubmit} variant="primary" size="sm">
                            <Save className="w-3.5 h-3.5 mr-1" />
                            {editingExpertiseId ? 'Update' : 'Save'}
                          </Button>
                          <Button type="button" onClick={resetExpertiseForm} variant="outline" size="sm">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {!mentorExpertiseList || mentorExpertiseList.length === 0 ? (
                      <p className="text-center text-gray-400 py-4 text-xs">
                        No expertise added yet. Click "Add" to get started.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Array.isArray(mentorExpertiseList) && mentorExpertiseList.map((expertise) => (
                          <div key={expertise.id} className="p-3 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow bg-white flex flex-col">
                            <div className="flex justify-between items-start gap-2 mb-2">
                              <div className="flex flex-wrap items-center gap-1">
                                <span className="px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs">
                                  {expertise.industryName}
                                </span>
                                {expertise.isCustomJob && (
                                  <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Custom</span>
                                )}
                              </div>
                              <div className="flex gap-0.5 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setViewingExpertise(expertise)}
                                  className="p-1 text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                  title="View"
                                >
                                  <Eye className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleEditExpertise(expertise)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteExpertise(expertise.id)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-gray-800 mb-1 break-words">
                                {expertise.jobTitle}
                              </h4>
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {expertise.expertiseDescription}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              )}

            </div>
          </form>

          {/* Skills - Mentor (outside form to prevent nested form issues) */}
          {isMentor && (
            <div className="mt-4 space-y-3">
              <SkillsSection />
              <BankDetailsSection />
              
              {/* Documents Section */}
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-primary-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary-600" />
                  Verification Documents
                </h3>
                <p className="text-xs text-gray-600 mb-4">
                  Manage your verification documents. You can update any document - it will be sent for re-approval.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[DOCUMENT_TYPES.AADHAR, DOCUMENT_TYPES.DEGREE_CERTIFICATE, DOCUMENT_TYPES.EXPERIENCE_CERTIFICATE].map((docType) => {
                    const uploadedDoc = getUploadedDoc(docType);
                    // Allow update for all documents (approved, pending, rejected, or not uploaded)
                    const canUpdate = true;

                    return (
                      <div key={docType} className="p-3 border border-gray-200 rounded-lg bg-gray-50 flex flex-col">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-primary-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <h4 className="text-sm font-semibold text-primary-700 truncate">{getDocumentLabel(docType)}</h4>
                              {uploadedDoc && (
                                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                  {getStatusIcon(uploadedDoc.status)}
                                  <span className="text-xs text-gray-600">{getStatusText(uploadedDoc.status)}</span>
                                  {uploadedDoc.isUpdated && (
                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">
                                      Updated
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {uploadedDoc?.status === DOCUMENT_STATUS.REJECTED && (
                          <div className="mb-2 p-2 bg-red-50 rounded border border-red-200">
                            <p className="text-xs text-red-800 font-medium mb-0.5">Rejection Reason:</p>
                            <p className="text-xs text-red-700 break-words">{uploadedDoc.rejectionReason}</p>
                          </div>
                        )}

                        {uploadedDoc && uploadedDoc.filePath && (
                          <div className="mb-2">
                            <a
                              href={uploadedDoc.filePath}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View Document
                            </a>
                          </div>
                        )}

                        {uploadedDoc?.status === DOCUMENT_STATUS.APPROVED && (
                          <div className="mb-2 p-2 bg-blue-50 rounded border border-blue-200">
                            <p className="text-xs text-blue-800">
                              This document is approved. Updating will require admin re-approval.
                            </p>
                          </div>
                        )}

                        {canUpdate ? (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {uploadedDoc ? 'Upload New Document' : 'Upload Document'} (JPEG, PNG, PDF - Max 5MB)
                              </label>
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                                onChange={(e) => handleFileSelect(docType, e.target.files[0])}
                                className="w-full max-w-full text-xs text-gray-600
                                  file:mr-3 file:py-1.5 file:px-3
                                  file:rounded-lg file:border-0
                                  file:text-xs file:font-semibold
                                  file:bg-primary-100 file:text-primary-700
                                  hover:file:bg-primary-200
                                  cursor-pointer"
                              />
                              {selectedFiles[docType] && (
                                <p className="mt-1 text-xs text-green-600 truncate">
                                  Selected: {selectedFiles[docType].name}
                                </p>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => handleDocumentUpload(docType)}
                              disabled={!selectedFiles[docType] || uploadingDoc === docType}
                              className="px-3 py-1.5 bg-secondary-500 text-white rounded-lg text-xs font-medium hover:bg-secondary-600 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {uploadingDoc === docType ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-3.5 h-3.5" />
                                  {uploadedDoc ? 'Update Document' : 'Upload Document'}
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="p-2 bg-green-50 rounded border border-green-200">
                            <div className="flex items-center gap-1.5">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <p className="text-xs text-green-800 font-medium">
                                Document approved - No changes allowed
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          )}

          {/* Additional Information - Students */}
          {isStudent && (
            <div className="mt-4 space-y-3">

              <div className="border-t border-gray-200 pt-3">
                <h2 className="text-base font-bold text-primary-800">Additional Information</h2>
                <p className="text-xs text-gray-600 mt-0.5">Complete your profile with skills, career focus and experience</p>
              </div>

              {/* Career Focus */}
              <CareerFocusSection 
                currentIndustryId={profile?.careerFocusIndustryId} 
                onUpdate={fetchProfile}
              />

              {/* Skills + Personality Traits */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
                <SkillsSection />
                <PersonalityTraitsSection />
              </div>

              {/* Experience */}
              <ExperienceSection />
            </div>
          )}
      </div>

      {/* Expertise View Modal */}
      {viewingExpertise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h3 className="text-base font-bold text-primary-800 flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary-600" />
                Expertise Details
              </h3>
              <button
                onClick={() => setViewingExpertise(null)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="px-3 py-1.5 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium">
                  {viewingExpertise.industryName}
                </span>
                {viewingExpertise.isCustomJob && (
                  <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium">
                    Custom Job
                  </span>
                )}
              </div>

              <div className="mb-6">
                <h4 className="text-xl font-bold text-primary-800 mb-2 break-words">
                  {viewingExpertise.jobTitle}
                </h4>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-4 sm:p-5">
                <h5 className="text-sm font-semibold text-primary-800 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  What I'm Offering / My Expertise
                </h5>
                <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                  {viewingExpertise.expertiseDescription}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  onClick={() => {
                    setViewingExpertise(null);
                    handleEditExpertise(viewingExpertise);
                  }}
                  variant="outline"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  type="button"
                  onClick={() => setViewingExpertise(null)}
                  variant="primary"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <Info className="w-4 h-4 text-primary-700" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-primary-800 truncate">Expertise Description Template</h3>
              </div>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Use this template as a guide to write your expertise description. Replace the placeholders with your actual information.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-4 sm:p-5">
                <div className="space-y-3 text-sm text-gray-800 leading-relaxed">
                  <p>
                    Hi, I'm <span className="font-semibold text-primary-700">[Your Name]</span>, working as a <span className="font-semibold text-primary-700">[Your Job Role]</span> in the <span className="font-semibold text-primary-700">[Your Industry]</span> sector with <span className="font-semibold text-primary-700">[X]</span> years of experience.
                  </p>
                  
                  <p>
                    I specialize in <span className="font-semibold text-primary-700">[Skill 1]</span>, <span className="font-semibold text-primary-700">[Skill 2]</span>, <span className="font-semibold text-primary-700">[Skill 3]</span>, and <span className="font-semibold text-primary-700">[Skill 4 (optional)]</span>.
                  </p>
                  
                  <p>
                    I have experience in <span className="font-semibold text-primary-700">[Type of work: field work / technical work / management / creative work / analytical work]</span>.
                  </p>
                  
                  <p className="pt-2">
                    I offer mentoring in:
                  </p>
                  <ul className="list-none space-y-1 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">Yes</span>
                      <span><span className="font-semibold text-primary-700">[Service 1]</span> - e.g., Skill Teaching</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">Yes</span>
                      <span><span className="font-semibold text-primary-700">[Service 2]</span> - e.g., Career Guidance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">Yes</span>
                      <span><span className="font-semibold text-primary-700">[Service 3]</span> - e.g., Interview Preparation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">Yes</span>
                      <span><span className="font-semibold text-primary-700">[Service 4 (optional)]</span></span>
                    </li>
                  </ul>
                  
                  <p className="pt-2">
                    I help people achieve <span className="font-semibold text-primary-700">[Goal: job readiness / career growth / skill improvement / confidence building / project execution]</span>.
                  </p>
                  
                  <p>
                    If you are looking for guidance in <span className="font-semibold text-primary-700">[Industry or Skill Area]</span>, I can help you with practical steps and real-world experience.
                  </p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Tips for Writing Your Expertise
                </h4>
                <ul className="text-xs text-blue-800 space-y-1.5 ml-5 list-disc">
                  <li>Be specific about your skills and experience</li>
                  <li>Mention concrete services you can provide</li>
                  <li>Highlight what makes you unique as a mentor</li>
                  <li>Keep it professional yet approachable</li>
                  <li>Focus on how you can help students achieve their goals</li>
                </ul>
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  variant="primary"
                >
                  Got it!
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;